import ErrorBoundary from '@/components/ErrorBoundary';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { typeToComponent } from 'lib/utils';
import React from 'react';
import { Content, ContentProps } from 'types/content';
import { dynamicComponents, dynamicComponentsStandalone } from './dynamicComponents';

export default function component<T = ContentProps>(name: string) {
  const component = dynamicComponents[name] as React.FunctionComponent<T>;

  if (!component) {
    throw new Error(`Cannot find component (${name}). Restarting the server might fix it.`);
  }

  return component;
}

export function standaloneComponent<T extends StandaloneComponentProps>(name: string) {
  const component = dynamicComponentsStandalone[name] as StandaloneComponent<T>;

  if (!component) {
    throw new Error(`Cannot find component standalone (${name}). Restarting the server might fix it.`);
  }

  return component;
}

export function componentExists(name: string) {
  const component = dynamicComponents[name];

  return component !== undefined;
}

export function bindProps(contentData: Content): ContentProps {
  return {
    type: contentData.type,
    data: contentData.data,
    meta: contentData.meta,
    descendants: contentData.children,
  };
}

export function contentComponent(type: string) {
  const componentName = `Content${typeToComponent(type)}`;

  return componentExists(componentName) ? component(componentName) : component('ContentFallback');
}

export function renderContentComponent(contentData: Content) {
  const ContentComponent = contentComponent(contentData.type);

  return (
    <ErrorBoundary>
      <ContentComponent {...bindProps(contentData)} />
    </ErrorBoundary>
  );
}

export function postTypeComponent(type: string) {
  const componentName = `PostType${typeToComponent(type)}`;

  return componentExists(componentName) ? component(componentName) : component('PostTypeFallback');
}

export function renderPostTypeComponent(postData: Content) {
  const PostTypeComponent = postTypeComponent(postData.type);

  return (
    <ErrorBoundary>
      <PostTypeComponent {...bindProps(postData)} />
    </ErrorBoundary>
  );
}

export function renderBySelector(selector: string, children?: Content[]): React.JSX.Element | undefined {
  const componentData = children?.find((child) => child.meta.selector === selector);
  if (componentData) return renderContentComponent(componentData);
}
